import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ZoomIn, ZoomOut, Maximize2, Check, Plus, Move,
  Camera, DoorOpen, Bell, Lock, Radio, HelpCircle, X, Loader2,
} from 'lucide-react';
import useMapStore from '../stores/mapStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import './MapPlacement.css';

const DEVICE_TYPE_CONFIG = {
  CAMERA:   { icon: Camera,     color: '#3b82f6', label: 'Camera'   },
  DOOR:     { icon: DoorOpen,   color: '#10b981', label: 'Door'     },
  INTERCOM: { icon: Bell,       color: '#f59e0b', label: 'Intercom' },
  LOCK:     { icon: Lock,       color: '#8b5cf6', label: 'Lock'     },
  SENSOR:   { icon: Radio,      color: '#ec4899', label: 'Sensor'   },
  OTHER:    { icon: HelpCircle, color: '#6b7280', label: 'Device'   },
};

const GENERIC_TYPES = [
  { value: 'CAMERA',   label: 'Camera'   },
  { value: 'DOOR',     label: 'Door'     },
  { value: 'INTERCOM', label: 'Intercom' },
  { value: 'LOCK',     label: 'Lock'     },
  { value: 'SENSOR',   label: 'Sensor'   },
];

const ZOOM_MIN  = 0.3;
const ZOOM_MAX  = 4;
const ZOOM_STEP = 0.15;

export default function MapPlacement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchFloorPlanById, upsertDeviceLocation, deleteDeviceLocation } = useMapStore();

  const [floorPlan, setFloorPlan]         = useState(null);
  const [placedDevices, setPlacedDevices] = useState([]);
  const [cameras, setCameras]             = useState([]);
  const [doors, setDoors]                 = useState([]);
  const [genericDevices, setGenericDevices] = useState([]);

  // Tab state: top = 'ccm' | 'generic', sub = 'cameras' | 'doors'
  const [topTab, setTopTab]   = useState('ccm');
  const [subTab, setSubTab]   = useState('cameras');

  // Add device form
  const [showAddForm, setShowAddForm]   = useState(false);
  const [newName, setNewName]           = useState('');
  const [newType, setNewType]           = useState('CAMERA');
  const [newNotes, setNewNotes]         = useState('');

  const [saving, setSaving]     = useState(false);
  const [zoom, setZoom]         = useState(1);
  const [pan, setPan]           = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingDevice, setPendingDevice]   = useState(null);
  const [movingDevice, setMovingDevice]     = useState(null);
  const [search, setSearch]     = useState('');
  const [rotatingDevice, setRotatingDevice] = useState(null);
  const [pendingRotation, setPendingRotation] = useState(0);
  const [pendingFov, setPendingFov] = useState(90);
  const [pendingRange, setPendingRange] = useState(70);

  const containerRef   = useRef(null);
  const imageRef       = useRef(null);
  const panStart       = useRef(null);
  const draggingDevice = useRef(null);
  const mouseDownPos = useRef(null);

  useEffect(() => {
    const main = document.querySelector('.main-content');
    if (main) { main.style.overflow = 'hidden'; main.style.padding = '0'; }
    return () => {
      if (main) { main.style.overflow = ''; main.style.padding = ''; }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchFloorPlanById(id);
      if (!cancelled && data) {
        setFloorPlan(data);
        setPlacedDevices(data.deviceLocations || []);
        setTimeout(() => fitToView(), 100);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [camRes, doorRes] = await Promise.all([
          fetchWithTenant('/api/vms/cameras'),
          fetchWithTenant('/api/feenics/readers'),
        ]);
        const camData  = camRes.ok  ? await camRes.json()  : {};
        const doorData = doorRes.ok ? await doorRes.json() : {};
        setCameras(camData.cameras || []);
        setDoors(doorData.readers || doorData.doors || []);
      } catch (err) {
        console.error('[MapPlacement] Failed to load devices:', err);
      }
    })();
  }, []);

  const fitToView = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    const naturalW = img.naturalWidth || img.width || 800;
    const naturalH = img.naturalHeight || img.height || 600;
    const scaleX = (container.width  - 40) / naturalW;
    const scaleY = (container.height - 80) / naturalH;
    setZoom(Math.min(scaleX, scaleY) * 0.80);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const onMouseDown = (e) => {
    if (e.button !== 0 || draggingDevice.current) return;
    setIsPanning(true);
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!isPanning || !panStart.current) return;
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  };
  const onMouseUp = () => { setIsPanning(false); panStart.current = null; };

  const selectDeviceForPlacement = (device, deviceType) => {
    setPendingDevice({ ...device, deviceType });
  };

  const onCanvasClick = useCallback(async (e) => {
    if (!pendingDevice && !movingDevice) return;
    if (!imageRef.current) return;
    // Ignore if mouse moved significantly (was a pan, not a click)
    if (mouseDownPos.current) {
      const dx = Math.abs(e.clientX - mouseDownPos.current.x);
      const dy = Math.abs(e.clientY - mouseDownPos.current.y);
      if (dx > 5 || dy > 5) return;
    }
    const isMove = !!movingDevice;
    const device = isMove ? movingDevice : pendingDevice;
    if (isMove) setMovingDevice(null); else setPendingDevice(null);
    const imgRect = imageRef.current.getBoundingClientRect();
    const floorX  = Math.min(100, Math.max(0, (e.clientX - imgRect.left) / imgRect.width  * 100));
    const floorY  = Math.min(100, Math.max(0, (e.clientY - imgRect.top)  / imgRect.height * 100));
    const payload = isMove ? {
      id:            device.id,
      floorPlanId:   id,
      deviceType:    device.deviceType,
      deviceId:      device.deviceId || null,
      deviceName:    device.deviceName || 'Unknown',
      integrationId: device.integrationId || null,
      notes:         device.notes || null,
      floorX,
      floorY,
      rotation:      device.rotation || 0,
      fovAngle:      device.fovAngle ?? 90,
      fovRange:      device.fovRange ?? 70,
    } : {
      floorPlanId:   id,
      deviceType:    device.deviceType,
      deviceId:      device.id || device.deviceId || device.Key || null,
      deviceName:    device.name || device.deviceName || device.Name || 'Unknown',
      integrationId: device.integrationId || device.externalId || null,
      notes:         device.notes || null,
      floorX,
      floorY,
      rotation: 0,
    };
    setSaving(true);
    try {
      const saved = await upsertDeviceLocation(payload);
      if (saved) {
        setPlacedDevices((prev) => {
          const idx = prev.findIndex((d) => d.id === saved.id);
          if (idx >= 0) { const u = [...prev]; u[idx] = saved; return u; }
          return [...prev, saved];
        });
        if (!isMove && saved && device.deviceType === 'CAMERA') handleRotatingDeviceOpen(saved);
        if (device._genericId) {
          setGenericDevices((prev) =>
            prev.map((g) => g._genericId === device._genericId ? { ...g, placed: true } : g)
          );
        }
      }
    } catch (err) {
      console.error('[MapPlacement] onCanvasClick error:', err);
    } finally {
      setSaving(false);
    }
  }, [pendingDevice, movingDevice, id, upsertDeviceLocation]);

  const onSidebarDragStart = (e, device, deviceType) => {
    draggingDevice.current = { ...device, deviceType };
    e.dataTransfer.setData('text/plain', JSON.stringify({ ...device, deviceType }));
    e.dataTransfer.effectAllowed = 'copy';
  };
  const onSidebarDragEnd = () => { draggingDevice.current = null; };

  const onCanvasDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const onCanvasDragLeave = () => setDragOver(false);

  const onCanvasDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    let device = draggingDevice.current;
    if (!device) {
      try { device = JSON.parse(e.dataTransfer.getData('text/plain')); } catch(err) {}
    }
    if (!device || !imageRef.current) return;

    const imgRect = imageRef.current.getBoundingClientRect();
    const floorX  = Math.min(100, Math.max(0, (e.clientX - imgRect.left) / imgRect.width  * 100));
    const floorY  = Math.min(100, Math.max(0, (e.clientY - imgRect.top)  / imgRect.height * 100));

    const payload = {
      floorPlanId:   id,
      deviceType:    device.deviceType,
      deviceId:      device.id || device.deviceId || device.Key || null,
      deviceName:    device.name || device.deviceName || device.Name || 'Unknown',
      integrationId: device.integrationId || device.externalId || null,
      notes:         device.notes || null,
      floorX,
      floorY,
      rotation: 0,
    };

    setSaving(true);
    try {
      const saved = await upsertDeviceLocation(payload);
      if (saved) {
        setPlacedDevices((prev) => {
          const idx = prev.findIndex((d) => d.id === saved.id);
          if (idx >= 0) { const u = [...prev]; u[idx] = saved; return u; }
          return [...prev, saved];
        });
        // Mark generic device as placed
        if (saved && device.deviceType === 'CAMERA') handleRotatingDeviceOpen(saved);
        if (device._genericId) {
          setGenericDevices((prev) =>
            prev.map((g) => g._genericId === device._genericId ? { ...g, placed: true } : g)
          );
        }
      }
    } catch (err) {
      console.error('[MapPlacement] upsertDeviceLocation error:', err);
    } finally {
      setSaving(false);
      draggingDevice.current = null;
    }
  }, [id, upsertDeviceLocation]);

  const handleRemoveDevice = async (deviceLocId) => {
    try {
      await deleteDeviceLocation(deviceLocId);
      setPlacedDevices((prev) => prev.filter((d) => d.id !== deviceLocId));
    } catch (err) {
      console.error('[MapPlacement] deleteDeviceLocation error:', err);
    }
  };

  const handleAddGeneric = () => {
    if (!newName.trim()) return;
    const generic = {
      _genericId:  `generic-${Date.now()}`,
      id:          null,
      name:        newName.trim(),
      deviceType:  newType,
      notes:       newNotes.trim() || null,
      placed:      false,
    };
    setGenericDevices((prev) => [...prev, generic]);
    setNewName('');
    setNewNotes('');
    setNewType('CAMERA');
    setShowAddForm(false);
  };

  const handleRemoveGeneric = (genericId) => {
    setGenericDevices((prev) => prev.filter((g) => g._genericId !== genericId));
  };

  const handleRotatingDeviceOpen = (saved) => {
    setRotatingDevice(saved);
    setPendingRotation(saved.rotation || 0);
    setPendingFov(saved.fovAngle ?? 90);
    setPendingRange(saved.fovRange ?? 70);
  };

  const handleRotationConfirm = useCallback(async (rotation, fovAngle, fovRange) => {
    const device = rotatingDevice;
    setRotatingDevice(null);
    try {
      const saved = await upsertDeviceLocation({
        id:            device.id,
        floorPlanId:   id,
        deviceType:    device.deviceType,
        deviceId:      device.deviceId   || null,
        deviceName:    device.deviceName || 'Unknown',
        integrationId: device.integrationId || null,
        notes:         device.notes || null,
        floorX:        device.floorX,
        floorY:        device.floorY,
        rotation,
        fovAngle,
        fovRange,
      });
      if (saved) setPlacedDevices((prev) => prev.map((d) => d.id === saved.id ? saved : d));
    } catch (err) {
      console.error('[MapPlacement] rotation upsert error:', err);
    }
  }, [rotatingDevice, id, upsertDeviceLocation]);

  const handleRotationSkip = () => setRotatingDevice(null);

  const placedIds       = new Set(placedDevices.map((d) => d.deviceId).filter(Boolean));
  const filteredCameras = cameras.filter((c) => (c.name || c.deviceName || '').toLowerCase().includes(search.toLowerCase()));
  const filteredDoors   = doors.filter((d)   => (d.Name || d.name || d.description || '').toLowerCase().includes(search.toLowerCase()));
  const filteredGeneric = genericDevices.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  if (!floorPlan) {
    return (
      <div className="mp-loading">
        <Loader2 size={28} className="spin" />
        <span>Loading map…</span>
      </div>
    );
  }

  return (
    <div className="mp-root">
      {/* Topbar */}
      <div className="mp-topbar">
        <div className="mp-topbar-left">
          <button className="mp-back-btn" onClick={() => navigate(`/map/survey/${id}`)}>
            <ArrowLeft size={16} /> Back
          </button>
          <span className="mp-title">{floorPlan.surveyLabel || floorPlan.name || 'Site Map'}</span>
          <span className="mp-badge">Place Devices</span>
        </div>
        <div className="mp-topbar-right">
          {saving && <Loader2 size={16} className="spin" />}
          <span className="mp-placed-count">{placedDevices.length} placed</span>
          <button className="btn-primary btn-sm" onClick={() => navigate(`/map/survey/${id}`)}>
            <Check size={14} /> Done
          </button>
        </div>
      </div>

      <div className="mp-body">
        {/* Sidebar */}
        <div className="mp-sidebar">

          {/* Top-level tabs */}
          <div className="mp-top-tabs">
            <button className={`mp-top-tab ${topTab === 'ccm' ? 'mp-top-tab--active' : ''}`} onClick={() => setTopTab('ccm')}>
              CCM Devices
            </button>
            <button className={`mp-top-tab ${topTab === 'generic' ? 'mp-top-tab--active' : ''}`} onClick={() => setTopTab('generic')}>
              Generic
            </button>
          </div>

          {/* CCM sub-tabs */}
          {topTab === 'ccm' && (
            <div className="mp-sidebar-tabs">
              <button className={`mp-tab ${subTab === 'cameras' ? 'mp-tab--active' : ''}`} onClick={() => setSubTab('cameras')}>
                <Camera size={13} /> Cameras ({cameras.length})
              </button>
              <button className={`mp-tab ${subTab === 'doors' ? 'mp-tab--active' : ''}`} onClick={() => setSubTab('doors')}>
                <DoorOpen size={13} /> Doors ({doors.length})
              </button>
            </div>
          )}

          {/* Generic header */}
          {topTab === 'generic' && (
            <div className="mp-generic-header">
              <span className="mp-generic-label">Manual Devices</span>
              <button className="mp-add-btn" onClick={() => setShowAddForm(true)}>
                <Plus size={13} /> Add Device
              </button>
            </div>
          )}

          {/* Add device form */}
          {topTab === 'generic' && showAddForm && (
            <div className="mp-add-form">
              <div className="mp-form-row">
                <label>Name</label>
                <input
                  type="text"
                  className="mp-form-input"
                  placeholder="e.g. Front Door Camera"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="mp-form-row">
                <label>Type</label>
                <select className="mp-form-input" value={newType} onChange={(e) => setNewType(e.target.value)}>
                  {GENERIC_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="mp-form-row">
                <label>Notes</label>
                <input
                  type="text"
                  className="mp-form-input"
                  placeholder="Optional"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                />
              </div>
              <div className="mp-form-actions">
                <button className="mp-form-cancel" onClick={() => { setShowAddForm(false); setNewName(''); setNewNotes(''); }}>Cancel</button>
                <button className="mp-form-save" onClick={handleAddGeneric} disabled={!newName.trim()}>Add</button>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="mp-sidebar-search">
            <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="mp-search-input" />
          </div>

          {/* Device list */}
          <div className="mp-device-list">
            {/* CCM Cameras */}
            {topTab === 'ccm' && subTab === 'cameras' && filteredCameras.map((cam) => {
              const isPlaced = placedIds.has(cam.id);
              return (
                <div key={cam.id} className={`mp-device-item ${isPlaced ? 'mp-device-item--placed' : ''}`}
                  draggable={!isPlaced} onDragStart={(e) => onSidebarDragStart(e, cam, 'CAMERA')} onDragEnd={onSidebarDragEnd}
                  title={isPlaced ? 'Already placed' : 'Click to select, then click map to place'} onClick={() => !isPlaced && selectDeviceForPlacement(cam, 'CAMERA')}>
                  <div className="mp-device-icon" style={{ '--marker-color': '#3b82f6' }}><Camera size={13} /></div>
                  <span className="mp-device-name">{cam.name || cam.deviceName || cam.id}</span>
                  {isPlaced && <span className="mp-placed-badge">✓</span>}
                </div>
              );
            })}
            {/* CCM Doors */}
            {topTab === 'ccm' && subTab === 'doors' && filteredDoors.map((door) => {
              const doorId   = door.Key || door.id || door.doorId;
              const isPlaced = placedIds.has(doorId);
              return (
                <div key={doorId} className={`mp-device-item ${isPlaced ? 'mp-device-item--placed' : ''}`}
                  draggable={!isPlaced} onDragStart={(e) => onSidebarDragStart(e, { ...door, id: doorId, name: door.Name || door.name }, 'DOOR')} onDragEnd={onSidebarDragEnd} onClick={() => !isPlaced && selectDeviceForPlacement({ ...door, id: doorId, name: door.Name || door.name }, 'DOOR')}
                  title={isPlaced ? 'Already placed' : 'Drag onto map'}>
                  <div className="mp-device-icon" style={{ '--marker-color': '#10b981' }}><DoorOpen size={13} /></div>
                  <span className="mp-device-name">{door.Name || door.name || door.description || doorId}</span>
                  {isPlaced && <span className="mp-placed-badge">✓</span>}
                </div>
              );
            })}
            {/* Generic devices */}
            {topTab === 'generic' && filteredGeneric.map((g) => {
              const cfg  = DEVICE_TYPE_CONFIG[g.deviceType] || DEVICE_TYPE_CONFIG.OTHER;
              const Icon = cfg.icon;
              return (
                <div key={g._genericId} className={`mp-device-item ${g.placed ? 'mp-device-item--placed' : ''}`}
                  draggable={!g.placed} onDragStart={(e) => onSidebarDragStart(e, g, g.deviceType)} onDragEnd={onSidebarDragEnd} onClick={() => !g.placed && selectDeviceForPlacement(g, g.deviceType)}
                  title={g.placed ? 'Already placed' : 'Drag onto map'}>
                  <div className="mp-device-icon" style={{ '--marker-color': cfg.color }}><Icon size={13} /></div>
                  <div className="mp-device-info">
                    <span className="mp-device-name">{g.name}</span>
                    <span className="mp-device-sub">{cfg.label}{g.notes ? ` · ${g.notes}` : ''}</span>
                  </div>
                  {g.placed
                    ? <span className="mp-placed-badge">✓</span>
                    : <button className="mp-remove-generic-btn" onClick={() => handleRemoveGeneric(g._genericId)} title="Remove"><X size={10} /></button>
                  }
                </div>
              );
            })}
            {/* Empty states */}
            {topTab === 'ccm'     && subTab === 'cameras' && filteredCameras.length === 0 && <div className="mp-empty">No cameras found</div>}
            {topTab === 'ccm'     && subTab === 'doors'   && filteredDoors.length   === 0 && <div className="mp-empty">No doors found</div>}
            {topTab === 'generic' && filteredGeneric.length === 0 && !showAddForm         && <div className="mp-empty">No devices yet — click Add Device</div>}
          </div>
        </div>

        {/* Canvas */}
        <div ref={containerRef} className={`mp-canvas ${isPanning ? 'is-panning' : ''} ${dragOver ? 'mp-canvas--dragover' : ''} ${(pendingDevice || movingDevice) ? 'mp-canvas--placing' : ''}`}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
          onDragOver={onCanvasDragOver} onDragLeave={onCanvasDragLeave} onDrop={onCanvasDrop} onClick={onCanvasClick}>
          <div className="mp-transform-layer" style={{ position: 'absolute', top: '50%', left: '50%', transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`, transformOrigin: 'center center' }}>
            <div className="mp-image-wrapper" onDragOver={(e) => e.preventDefault()}>
              <img ref={imageRef} src={floorPlan.imageUrl} alt={floorPlan.surveyLabel} draggable={false} onLoad={fitToView} className="mp-floor-image" />
              {placedDevices.map((device) => {
                if (device.floorX == null || device.floorY == null) return null;
                const cfg  = DEVICE_TYPE_CONFIG[device.deviceType] || DEVICE_TYPE_CONFIG.OTHER;
                const Icon = cfg.icon;
                const isCamera = device.deviceType === 'CAMERA';
                const isBeingConfigured = rotatingDevice && rotatingDevice.id === device.id;
                const rot = isBeingConfigured ? pendingRotation : (device.rotation || 0);
                const fov = isBeingConfigured ? pendingFov : (device.fovAngle ?? 90);
                const range = isBeingConfigured ? pendingRange : (device.fovRange ?? 70);
                return (
                  <div key={device.id} style={{ position: 'absolute', left: `${device.floorX}%`, top: `${device.floorY}%`, width: 0, height: 0, overflow: 'visible' }}>
                    {isCamera && <FovCone rotation={rot} fovAngle={fov} fovRange={range} color={cfg.color} />}
                    <div className="mp-placed-marker" style={{ '--marker-color': cfg.color, position: 'absolute', transform: 'translate(-50%, -50%)', cursor: isCamera ? 'pointer' : 'default' }}
                      title={device.deviceName || cfg.label}
                      onClick={isCamera ? (e) => { e.stopPropagation(); handleRotatingDeviceOpen(device); } : undefined}>
                      <Icon size={13} />
                      <button className="mp-move-btn" onClick={(e) => { e.stopPropagation(); setMovingDevice(device); setPendingDevice(null); setRotatingDevice(null); }} onMouseDown={(e) => { e.stopPropagation(); mouseDownPos.current = null; }} title="Move"><Move size={9} /></button>
                      <button className="mp-remove-btn" onClick={(e) => { e.stopPropagation(); handleRemoveDevice(device.id); }} title="Remove"><X size={9} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
            {rotatingDevice && (
              <CameraConfigPopover
                device={rotatingDevice}
                rotation={pendingRotation}
                fovAngle={pendingFov}
                fovRange={pendingRange}
                onRotationChange={setPendingRotation}
                onFovChange={setPendingFov}
                onRangeChange={setPendingRange}
                onConfirm={handleRotationConfirm}
                onSkip={handleRotationSkip}
              />
            )}
          <div className="mp-zoom-controls">
            <button className="zoom-btn" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}><ZoomIn size={16} /></button>
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            <button className="zoom-btn" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}><ZoomOut size={16} /></button>
            <div className="zoom-divider" />
            <button className="zoom-btn" onClick={fitToView}><Maximize2 size={16} /></button>
          </div>
          {dragOver && <div className="mp-drop-hint">Drop to place device</div>}
        </div>
      </div>
    </div>
  );
}

function FovCone({ rotation, fovAngle, fovRange, color }) {
  const r = fovRange ?? 70;
  const half = fovAngle / 2;
  // Build polygon points for clip-path: apex at center-bottom of box,
  // fan upward. We use a 200x200 box, apex at (50%, 50%).
  const steps = 24;
  const points = [`50% 50%`];
  for (let i = 0; i <= steps; i++) {
    const angle = (-half + (fovAngle * i / steps)) * (Math.PI / 180);
    const px = 50 + 50 * Math.sin(angle);
    const py = 50 - 50 * Math.cos(angle);
    points.push(`${px.toFixed(2)}% ${py.toFixed(2)}%`);
  }
  const clipPath = `polygon(${points.join(', ')})`;
  const size = r * 2;
  return (
    <div style={{
      position: 'absolute',
      left: `-${r}px`,
      top: `-${r}px`,
      width: `${size}px`,
      height: `${size}px`,
      background: color,
      opacity: 0.3,
      clipPath,
      transform: `rotate(${rotation}deg)`,
      transformOrigin: `${r}px ${r}px`,
      pointerEvents: 'none',
      zIndex: 0,
      borderRadius: 0,
    }} />
  );
}

function CameraConfigPopover({ device, rotation, fovAngle, fovRange, onRotationChange, onFovChange, onRangeChange, onConfirm, onSkip }) {
  const cfg = DEVICE_TYPE_CONFIG.CAMERA;
  const clamp = (v) => ((v % 360) + 360) % 360;
  const adjust = (delta) => onRotationChange((r) => clamp(r + delta));
  const setRotation = onRotationChange;
  const setFovAngle = onFovChange;

  return (
    <div className="mp-rotation-popover" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onMouseMove={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
      <div className="mp-rp-header">
        <div className="mp-rp-device-icon" style={{ '--marker-color': cfg.color, transform: `rotate(${rotation}deg)`, transition: 'transform 0.08s linear' }}>
          <Camera size={15} />
        </div>
        <div className="mp-rp-title">
          <span className="mp-rp-name">{device.deviceName || 'Camera'}</span>
          <span className="mp-rp-sub">Direction &amp; field of view</span>
        </div>
        <span className="mp-rp-deg-badge">{rotation}° · {fovAngle}°</span>
      </div>
      <div className="mp-rp-body">
        <div className="mp-rp-section-label">Rotation</div>
        <div className="mp-rp-presets">
          {[0, 90, 180, 270].map((deg) => (
            <button key={deg} className={`mp-rp-preset ${rotation === deg ? 'mp-rp-preset--active' : ''}`} onClick={() => setRotation(deg)}>
              {deg}°
            </button>
          ))}
        </div>
        <div className="mp-rp-controls">
          <button className="mp-rp-adj-btn" onClick={() => adjust(-15)}>−15</button>
          <button className="mp-rp-adj-btn" onClick={() => adjust(-1)}>−1</button>
          <input type="number" min={0} max={359} className="mp-rp-input" value={rotation}
            onChange={(e) => setRotation(clamp(parseInt(e.target.value, 10) || 0))} />
          <button className="mp-rp-adj-btn" onClick={() => adjust(1)}>+1</button>
          <button className="mp-rp-adj-btn" onClick={() => adjust(15)}>+15</button>
        </div>
        <input type="range" min={0} max={359} value={rotation}
          onChange={(e) => setRotation(Number(e.target.value))} className="mp-rp-slider" />
        <div className="mp-rp-section-label">Field of View</div>
        <div className="mp-rp-presets">
          {[60, 90, 120, 180].map((deg) => (
            <button key={deg} className={`mp-rp-preset ${fovAngle === deg ? 'mp-rp-preset--active' : ''}`} onClick={() => setFovAngle(deg)}>
              {deg}°
            </button>
          ))}
        </div>
        <input type="range" min={10} max={270} value={fovAngle}
          onChange={(e) => setFovAngle(Number(e.target.value))} className="mp-rp-slider" />
        <div className="mp-rp-section-label">Coverage Distance</div>
        <div className="mp-rp-range-row">
          <span className="mp-rp-range-val">Near</span>
          <input type="range" min={30} max={200} value={fovRange}
            onChange={(e) => onRangeChange(Number(e.target.value))} className="mp-rp-slider" />
          <span className="mp-rp-range-val">Far</span>
        </div>
      </div>
      <div className="mp-rp-footer">
        <button className="mp-rp-skip" onClick={onSkip}>Skip</button>
        <button className="mp-rp-confirm" onClick={() => onConfirm(rotation, fovAngle, fovRange)}>
          <Check size={13} /> Confirm
        </button>
      </div>
    </div>
  );
}
